import type { ComponentMeta } from "@mijalowcode/types";
import { ComponentCategory, PropType } from "@mijalowcode/types";

export const ListMeta: ComponentMeta = {
  componentName: "List",
  package: "@mijadesign/web",
  title: "列表",
  description: "展示数据列表",
  category: ComponentCategory.DATA_DISPLAY,
  icon: "UnorderedListOutlined",
  canHaveChildren: true,
  visibleInComponentPanel: true,
  editorDnd: {
    draggable: true,
    dropMode: "children",
  },
  designToken: {
    fields: [
      {
        name: "itemPadding",
        label: "列表项内边距",
        type: "string",
        placeholder: "如 12px 24px",
      },
      { name: "itemPaddingSM", label: "小尺寸内边距", type: "string" },
      { name: "itemPaddingLG", label: "大尺寸内边距", type: "string" },
    ],
  },
  props: [
    {
      name: "dataSource",
      label: "数据源",
      type: PropType.JSON,
      bindable: true,
      bindSources: ["state", "record"],
      defaultValue: [
        { id: "1", title: "第一项", description: "列表项描述" },
        { id: "2", title: "第二项", description: "列表项描述" },
      ],
      description: "数组数据；存在子物料时，子物料会作为每一项的渲染模板",
      group: "数据",
    },
    {
      name: "rowKey",
      label: "行唯一键",
      type: PropType.STRING,
      defaultValue: "id",
      description: "每条数据用于生成 key 的字段",
      group: "数据",
    },
    {
      name: "size",
      label: "尺寸",
      type: PropType.ENUM,
      defaultValue: "default",
      options: [
        { label: "大", value: "large" },
        { label: "默认", value: "default" },
        { label: "小", value: "small" },
      ],
      group: "样式",
    },
    {
      name: "itemLayout",
      label: "列表项布局",
      type: PropType.ENUM,
      defaultValue: "horizontal",
      options: [
        { label: "横排", value: "horizontal" },
        { label: "竖排", value: "vertical" },
      ],
      description: "控制 List.Item 内容布局方向",
      group: "样式",
    },
    {
      name: "layoutMode",
      label: "布局模式",
      type: PropType.ENUM,
      defaultValue: "grid",
      options: [
        { label: "栅格", value: "grid" },
        { label: "Flex", value: "flex" },
      ],
      description: "栅格布局或 Flex 换行布局",
      group: "样式",
    },
    {
      name: "grid.column",
      label: "网格列数",
      type: PropType.NUMBER,
      defaultValue: 1,
      min: 1,
      max: 24,
      step: 1,
      description: "List 网格布局的 column 字段",
      group: "样式",
      visibleWhen: {
        field: "layoutMode",
        equals: "grid",
      },
    },
    {
      name: "grid.gutter",
      label: "网格间距",
      type: PropType.NUMBER,
      defaultValue: 16,
      min: 0,
      max: 96,
      step: 1,
      description: "List 网格布局的 gutter 字段",
      group: "样式",
      visibleWhen: {
        field: "layoutMode",
        equals: "grid",
      },
    },
    {
      name: "flex.columnGap",
      label: "列间距(px)",
      type: PropType.NUMBER,
      defaultValue: 16,
      min: 0,
      max: 96,
      step: 1,
      description: "Flex 换行时列表项之间的横向间距",
      group: "样式",
      visibleWhen: {
        field: "layoutMode",
        equals: "flex",
      },
    },
    {
      name: "flex.rowGap",
      label: "行间距(px)",
      type: PropType.NUMBER,
      defaultValue: 16,
      min: 0,
      max: 96,
      step: 1,
      description: "Flex 换行时列表项之间的纵向间距",
      group: "样式",
      visibleWhen: {
        field: "layoutMode",
        equals: "flex",
      },
    },
    {
      name: "bordered",
      label: "边框",
      type: PropType.BOOLEAN,
      defaultValue: true,
      group: "样式",
    },
    {
      name: "split",
      label: "分割线",
      type: PropType.BOOLEAN,
      defaultValue: true,
      group: "样式",
    },
    {
      name: "loading",
      label: "加载中",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "状态",
    },
    {
      name: "pagination",
      label: "分页",
      type: PropType.BOOLEAN,
      defaultValue: false,
      group: "功能",
    },
  ],
  defaultProps: {
    bordered: true,
    split: true,
    layoutMode: "grid",
    itemLayout: "horizontal",
    grid: {
      column: 1,
      gutter: 16,
    },
    flex: {
      columnGap: 16,
      rowGap: 16,
    },
  },
  preview: [
    {
      title: "基础列表",
      props: {
        bordered: true,
        dataSource: [
          { id: "1", title: "第一项", description: "列表项描述" },
          { id: "2", title: "第二项", description: "列表项描述" },
        ],
        rowKey: "id",
      },
    },
    {
      title: "紧凑列表",
      props: {
        size: "small",
        split: true,
        dataSource: [
          { id: "1", title: "待处理任务", description: "今天 10:00 截止" },
          { id: "2", title: "已完成任务", description: "昨天已归档" },
        ],
        rowKey: "id",
      },
    },
  ],
  docLinks: [
    {
      title: "List 文档",
      url: "https://5x-https://5x-ant-design.antgroup.com//components/list-cn/",
      source: "antd",
    },
  ],
  npm: {
    package: "antd",
    exportName: "List",
  },
  snippet: {
    componentName: "List",
    package: "antd",
    props: {
      bordered: true,
      dataSource: [
        { id: "1", title: "第一项", description: "列表项描述" },
        { id: "2", title: "第二项", description: "列表项描述" },
      ],
      rowKey: "id",
      layoutMode: "grid",
      itemLayout: "horizontal",
      grid: {
        column: 1,
        gutter: 16,
      },
      flex: {
        columnGap: 16,
        rowGap: 16,
      },
    },
    children: [],
  },
};

export const ListItemMeta: ComponentMeta = {
  componentName: "List.Item",
  package: "@mijadesign/web",
  title: "列表项",
  description: "列表中的单项",
  category: ComponentCategory.DATA_DISPLAY,
  icon: "FileOutlined",
  canHaveChildren: true,
  props: [
    {
      name: "actions",
      label: "操作列表",
      type: PropType.JSON,
      group: "功能",
    },
    {
      name: "extra",
      label: "额外内容",
      type: PropType.STRING,
      group: "内容",
    },
  ],
  npm: {
    package: "@mijadesign/web",
    exportName: "List",
  },
  snippet: {
    componentName: "List.Item",
    package: "@mijadesign/web",
    props: {},
  },
};
